// client/src/pages/Admin/ResourceManager/components/ResourcePagination.tsx

import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { PAGE_SIZE_OPTIONS } from '../constants/resource.constants';

interface ResourcePaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  startIndex: number;
  endIndex: number;
  totalItems: number;
  pageNumbers: (number | string)[];
  onGoToPage: (page: number) => void;
  onGoToFirstPage: () => void;
  onGoToLastPage: () => void;
  onGoToPreviousPage: () => void;
  onGoToNextPage: () => void;
  onPageSizeChange: (size: number) => void;
}

export const ResourcePagination: React.FC<ResourcePaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  startIndex,
  endIndex,
  totalItems,
  pageNumbers,
  onGoToPage,
  onGoToFirstPage,
  onGoToLastPage,
  onGoToPreviousPage,
  onGoToNextPage,
  onPageSizeChange
}) => {
  if (totalItems === 0) return null;

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        <span className="pagination-text">
          Showing {startIndex} to {endIndex} of {totalItems} entries
        </span>
        <div className="pagination-size-control">
          <label htmlFor="page-size" className="page-size-label">
            Rows per page:
          </label>
          <select
            id="page-size"
            className="page-size-select"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="pagination-controls" role="navigation" aria-label="Pagination">
        <button
          type="button"
          className="pagination-btn"
          onClick={onGoToFirstPage}
          disabled={currentPage === 1}
          aria-label="First page"
        >
          <ChevronsLeft size={16} />
        </button>
        <button
          type="button"
          className="pagination-btn"
          onClick={onGoToPreviousPage}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>

        {pageNumbers.map((page, index) => (
          typeof page === 'number' ? (
            <button
              key={index}
              type="button"
              className={`pagination-btn page-number ${currentPage === page ? 'active' : ''}`}
              onClick={() => onGoToPage(page)}
              aria-label={`Page ${page}`}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </button>
          ) : (
            <span key={index} className="pagination-ellipsis">
              {page}
            </span>
          )
        ))}

        <button
          type="button"
          className="pagination-btn"
          onClick={onGoToNextPage}
          disabled={currentPage === totalPages || totalPages === 0}
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          className="pagination-btn"
          onClick={onGoToLastPage}
          disabled={currentPage === totalPages || totalPages === 0}
          aria-label="Last page"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </div>
  );
};